import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/** Scrollable drum column — no visible scrollbar */
const ScrollColumn = ({ items, selected, onSelect }) => {
  const containerRef = useRef(null);
  const itemH = 34;

  useEffect(() => {
    const idx = items.indexOf(selected);
    if (idx >= 0 && containerRef.current) {
      containerRef.current.scrollTop = idx * itemH - containerRef.current.clientHeight / 2 + itemH / 2;
    }
  }, [selected, items]);

  return (
    <div
      ref={containerRef}
      className="h-[170px] overflow-y-auto"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
    >
      <style>{`.dtp-scroll::-webkit-scrollbar { display: none; }`}</style>
      <div className="dtp-scroll" />
      {items.map(item => (
        <button
          key={item}
          type="button"
          onClick={() => onSelect(item)}
          className={`w-full text-center transition-all ${
            item === selected
              ? 'bg-gradient-to-r from-pest-blue to-pest-teal text-white font-bold rounded-lg shadow-sm'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 rounded-lg'
          }`}
          style={{ height: itemH, lineHeight: `${itemH}px`, fontSize: item === selected ? 18 : 15 }}
        >
          {item}
        </button>
      ))}
    </div>
  );
};

const TERM_OPTIONS = [
  { label: 'On Receipt', days: 0 },
  { label: 'Net 7', days: 7 },
  { label: 'Net 15', days: 15 },
  { label: 'Net 30', days: 30 },
];

const DateTimePicker = ({ value, onChange, className = '', dateOnly = false, showTerms = false, baseDate, onTermSelect, activeTerm }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  // dateOnly mode: value is just 'YYYY-MM-DD', datetime mode: 'YYYY-MM-DDTHH:MM'
  const datePart = value ? value.split('T')[0] : '';
  const timePart = !dateOnly && value ? (value.split('T')[1] || '') : '';
  const selHour = timePart ? timePart.split(':')[0] : '09';
  const selMin = timePart ? timePart.split(':')[1] : '00';

  const [viewDate, setViewDate] = useState(() => {
    if (datePart) return new Date(datePart + 'T12:00:00');
    return new Date();
  });

  useEffect(() => {
    if (datePart) setViewDate(new Date(datePart + 'T12:00:00'));
  }, [datePart]);

  // Pending selection — used when popup opens with no value so today is visually highlighted
  const [pendingDate, setPendingDate] = useState(null); // 'YYYY-MM-DD'
  const [pendingHour, setPendingHour] = useState(null);
  const [pendingMin, setPendingMin] = useState(null);

  useEffect(() => {
    if (open && !value) {
      const now = new Date();
      setPendingDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
      setPendingHour(String(now.getHours()).padStart(2, '0'));
      setPendingMin('00');
    } else {
      setPendingDate(null);
      setPendingHour(null);
      setPendingMin(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupH = dateOnly ? 340 : 480;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openDown = spaceBelow >= popupH || spaceBelow > rect.top;
    setPos({
      top: openDown ? rect.bottom + 4 + window.scrollY : rect.top - popupH - 4 + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, []);

  useEffect(() => {
    if (open) {
      updatePos();
      window.addEventListener('scroll', updatePos, true);
      window.addEventListener('resize', updatePos);
      return () => {
        window.removeEventListener('scroll', updatePos, true);
        window.removeEventListener('resize', updatePos);
      };
    }
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      const popup = document.getElementById('dtp-popup');
      if (popup?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayDate = datePart
    ? `${datePart.split('-')[2]}/${datePart.split('-')[1]}/${datePart.split('-')[0]}`
    : 'dd/mm/yyyy';
  const displayTime = (timePart && selHour && selMin) ? `${selHour}:${selMin}` : '--:--';

  const emit = (d, h, m) => {
    if (dateOnly) {
      onChange(d);
    } else {
      onChange(`${d}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  };

  const selectDay = (day) => {
    const y = viewDate.getFullYear();
    const mo = String(viewDate.getMonth() + 1).padStart(2, '0');
    const da = String(day).padStart(2, '0');
    const h = value ? selHour : (pendingHour || selHour);
    const m = value ? selMin : (pendingMin || selMin);
    emit(`${y}-${mo}-${da}`, h, m);
    setPendingDate(null);
    // Manual calendar pick clears any active term
    if (showTerms && onTermSelect) onTermSelect(null);
    if (dateOnly) setOpen(false);
  };

  const selectHour = (h) => {
    const d = datePart || pendingDate || new Date().toISOString().split('T')[0];
    emit(d, h, value ? selMin : (pendingMin || selMin));
    setPendingDate(null);
  };

  const selectMinute = (m) => {
    const d = datePart || pendingDate || new Date().toISOString().split('T')[0];
    emit(d, value ? selHour : (pendingHour || selHour), m);
    setPendingDate(null);
  };

  const prevMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const nextMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  // Build calendar — always 6 rows for fixed height
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let startDow = new Date(year, month, 1).getDay() - 1;
  if (startDow < 0) startDow = 6;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = startDow - 1; i >= 0; i--) cells.push({ day: prevMonthLastDay - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, current: true });
  // Always fill to 42 cells (6 rows)
  while (cells.length < 42) {
    cells.push({ day: cells.length - startDow - daysInMonth + 1, current: false });
  }

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const activeDatePart = datePart || pendingDate;
  const selectedDay = activeDatePart ? parseInt(activeDatePart.split('-')[2], 10) : null;
  const selectedMonth = activeDatePart ? parseInt(activeDatePart.split('-')[1], 10) - 1 : null;
  const selectedYear = activeDatePart ? parseInt(activeDatePart.split('-')[0], 10) : null;
  const isSelected = (day) => day === selectedDay && month === selectedMonth && year === selectedYear;

  const handleToday = () => {
    const t = new Date();
    setViewDate(t);
    emit(
      `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`,
      selHour, selMin
    );
  };

  const handleClear = () => { onChange(''); setOpen(false); };

  const popup = open ? ReactDOM.createPortal(
    <div
      id="dtp-popup"
      className="bg-white rounded-2xl shadow-2xl z-[9999] w-[300px] border border-gray-100 overflow-hidden"
      style={{ position: 'absolute', top: pos.top, left: pos.left }}
    >
      {/* Gradient header with month nav */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-pest-blue to-pest-teal">
        <button type="button" onClick={prevMonth} className="p-1 hover:bg-white/20 rounded-full transition-colors">
          <ChevronLeft size={18} className="text-white" />
        </button>
        <span className="text-sm font-bold text-white tracking-wide">{MONTHS[month]} {year}</span>
        <button type="button" onClick={nextMonth} className="p-1 hover:bg-white/20 rounded-full transition-colors">
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>

      {/* Payment term shortcuts */}
      {showTerms && baseDate && (
        <div className="flex gap-1 px-3 pt-2.5 pb-1">
          {TERM_OPTIONS.map(term => (
            <button
              key={term.label}
              type="button"
              onClick={() => {
                const base = new Date(baseDate + 'T12:00:00');
                base.setDate(base.getDate() + term.days);
                const d = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
                setViewDate(base);
                onChange(d);
                if (onTermSelect) onTermSelect(term.label === 'On Receipt' ? 'Due on receipt' : term.label);
                setOpen(false);
              }}
              className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                activeTerm === (term.label === 'On Receipt' ? 'Due on receipt' : term.label)
                  ? 'bg-pest-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {term.label}
            </button>
          ))}
        </div>
      )}

      {/* Day headers */}
      <div className="grid grid-cols-7 px-3 pt-2.5">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[11px] font-semibold text-pest-blue/60 py-1">{d}</div>
        ))}
      </div>

      {/* Day cells — fixed 6 rows */}
      <div className="grid grid-cols-7 px-3 pb-1">
        {cells.map((cell, i) => (
          <button
            key={i}
            type="button"
            onClick={() => cell.current && selectDay(cell.day)}
            disabled={!cell.current}
            className={`text-center py-1.5 text-sm rounded-full transition-all ${
              !cell.current
                ? 'text-gray-300'
                : isSelected(cell.day)
                  ? 'bg-gradient-to-r from-pest-blue to-pest-teal text-white font-bold shadow-sm'
                  : isToday(cell.day)
                    ? 'ring-2 ring-pest-teal text-pest-blue font-semibold'
                    : 'text-gray-700 hover:bg-pest-blue/10'
            }`}
          >
            {cell.day}
          </button>
        ))}
      </div>

      {/* Clear / Today */}
      <div className="flex justify-between px-4 pb-2.5 pt-1">
        <button type="button" onClick={handleClear} className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors">Clear</button>
        <button type="button" onClick={handleToday} className="text-xs text-pest-teal hover:text-pest-blue font-semibold transition-colors">Today</button>
      </div>

      {/* Time drums — hidden in dateOnly mode */}
      {!dateOnly && (
        <>
          <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 flex items-center justify-center gap-3">
            <div className="w-16 bg-white rounded-xl shadow-inner border border-gray-100">
              <ScrollColumn items={HOURS} selected={value ? selHour : (pendingHour || selHour)} onSelect={selectHour} />
            </div>
            <span className="text-xl font-bold text-pest-blue/40">:</span>
            <div className="w-16 bg-white rounded-xl shadow-inner border border-gray-100">
              <ScrollColumn items={MINUTES} selected={value ? selMin : (pendingMin || selMin)} onSelect={selectMinute} />
            </div>
          </div>
          <div className="px-4 pb-3 pt-1 bg-gray-50/50">
            <button
              type="button"
              onClick={() => {
                // Commit pending selection if user hasn't picked anything yet
                if (!value && pendingDate) {
                  emit(pendingDate, pendingHour || '09', pendingMin || '00');
                }
                setOpen(false);
              }}
              className="w-full py-2 bg-gradient-to-r from-pest-blue to-pest-teal text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Set
            </button>
          </div>
        </>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Hide scrollbar globally for the drums */}
      {open && <style>{`#dtp-popup .h-\\[170px\\]::-webkit-scrollbar { display: none; }`}</style>}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 border-2 border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors text-sm cursor-pointer w-full"
      >
        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
        <span className={`${value || activeTerm ? 'text-gray-700' : 'text-gray-400'} font-medium`}>
          {showTerms && activeTerm
            ? activeTerm
            : dateOnly ? displayDate : `${displayDate} ${displayTime}`}
        </span>
      </button>
      {popup}
    </div>
  );
};

export default DateTimePicker;
